import { useState } from "react";
import { t } from "@/lib/i18n/messages";
import { cn } from "@/lib/utils";
import { useDashboardPeriod, PeriodType } from "@/context/dashboard-period-context";
import type { Locale } from "@/types/auth";

interface PeriodSelectorProps {
  locale: Locale;
}

const PERIODS: PeriodType[] = ["daily", "weekly", "monthly", "custom"];

export function PeriodSelector({ locale }: PeriodSelectorProps) {
  const { period, customRange, setPeriod, setCustomRange } = useDashboardPeriod();
  const [localFrom, setLocalFrom] = useState(customRange?.from ?? "");
  const [localTo, setLocalTo] = useState(customRange?.to ?? "");
  const [dateError, setDateError] = useState<string | null>(null);

  const periodLabels: Record<PeriodType, string> = {
    daily: t(locale, "periodDaily"),
    weekly: t(locale, "periodWeekly"),
    monthly: t(locale, "periodMonthly"),
    custom: t(locale, "periodCustom"),
  };

  const handleApplyCustom = () => {
    if (!localFrom || !localTo) {
      setDateError("Please select both dates");
      return;
    }
    if (localFrom > localTo) {
      setDateError(`"${t(locale, "periodFrom")}" must be before "${t(locale, "periodTo")}"`);
      return;
    }
    setDateError(null);
    setCustomRange({ from: localFrom, to: localTo });
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      {/* Tab buttons */}
      <div
        className="flex rounded-lg border border-border bg-muted/30 p-1 gap-1"
        role="tablist"
        aria-label="Period"
      >
        {PERIODS.map((p) => (
          <button
            key={p}
            type="button"
            role="tab"
            aria-selected={period === p}
            onClick={() => setPeriod(p)}
            className={cn(
              "px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150",
              period === p
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-foreground-secondary hover:text-foreground hover:bg-muted"
            )}
          >
            {periodLabels[p]}
          </button>
        ))}
      </div>

      {/* Custom date range inputs */}
      {period === "custom" && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <label htmlFor="period-from" className="text-sm text-foreground-secondary whitespace-nowrap">
              {t(locale, "periodFrom")}
            </label>
            <input
              id="period-from"
              type="date"
              value={localFrom}
              onChange={(e) => {
                setLocalFrom(e.target.value);
                setDateError(null);
              }}
              className={cn(
                "text-sm border rounded-md px-2 py-1 bg-background text-foreground",
                "border-border focus:outline-none focus:ring-2 focus:ring-primary/40"
              )}
            />
          </div>
          <div className="flex items-center gap-1.5">
            <label htmlFor="period-to" className="text-sm text-foreground-secondary whitespace-nowrap">
              {t(locale, "periodTo")}
            </label>
            <input
              id="period-to"
              type="date"
              value={localTo}
              min={localFrom}
              onChange={(e) => {
                setLocalTo(e.target.value);
                setDateError(null);
              }}
              className={cn(
                "text-sm border rounded-md px-2 py-1 bg-background text-foreground",
                "border-border focus:outline-none focus:ring-2 focus:ring-primary/40"
              )}
            />
          </div>
          <button
            type="button"
            onClick={handleApplyCustom}
            className={cn(
              "px-3 py-1 rounded-md text-sm font-medium",
              "bg-primary text-primary-foreground hover:bg-primary/90",
              "transition-colors duration-150"
            )}
          >
            {t(locale, "confirm")}
          </button>
          {dateError && (
            <p className="text-xs text-red-600 dark:text-red-400">{dateError}</p>
          )}
        </div>
      )}
    </div>
  );
}
